import { Box, Slider, Stack, Switch, TextField, Typography } from '@chronosphereio/chrono-ui';
import { useOptimisticState } from '@chronosphereio/shared';
import { produce } from 'immer';
import { useCallback } from 'react';
import { TimeSeriesChartStepOption, TimeSeriesChartThresholdOptions } from '@/core';
import { OptionsEditorControl } from '@/plugin-editor-components';
import {
  ThresholdsEditor,
  ThresholdsEditorProps,
} from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';
import { DEFAULT_THRESHOLD_AREA_OPACITY } from '../time-series-chart-model';

// Ensure chart does not update too often, the area_opacity slider felt snappy with this value but it can be adjusted slightly based on feedback.
const SLIDER_INPUT_DELAY_MS = 200;

export const TimeSeriesChartThresholdsEditor = ({
  thresholds,
  onChange,
}: {
  thresholds?: TimeSeriesChartThresholdOptions;
  onChange: (newThresholds: TimeSeriesChartThresholdOptions) => void;
}) => {
  const props: ThresholdsEditorProps<TimeSeriesChartStepOption, TimeSeriesChartThresholdOptions> = {
    onChange,
    renderStepOptions: ({ step, onStepChange }) => {
      return <TimeSeriesChartThresholdStepEditor step={step} onStepChange={onStepChange} />;
    },
    thresholds,
    hideDefault: true,
  };

  return (
    <>
      <ThresholdsEditor {...props} />
      {thresholds !== undefined && thresholds.steps !== undefined && thresholds.steps.length > 0 && (
        <OptionsEditorControl
          label="Scale axis"
          description="Automatically expand the Y-axis range to include threshold values"
          control={
            <Switch
              checked={thresholds.scale_axis !== false}
              onChange={(_, checked) => {
                onChange(
                  produce(thresholds, (draft) => {
                    draft.scale_axis = checked;
                  })
                );
              }}
            />
          }
        />
      )}
    </>
  );
};

function TimeSeriesChartThresholdStepEditor<T extends Omit<TimeSeriesChartStepOption, 'value'>>({
  step,
  onStepChange,
}: {
  step: T;
  onStepChange: (newStep: T) => void;
}) {
  const handleLabelChange = useCallback(
    (newLabel: string) => {
      onStepChange(
        produce(step, (draft) => {
          return { ...draft, name: newLabel };
        })
      );
    },
    [step, onStepChange]
  );

  // Area opacity slider using optimistic state to improve perceived performance
  const [areaOpacity, setAreaOpacity] = useOptimisticState(
    step.area_opacity ?? DEFAULT_THRESHOLD_AREA_OPACITY,
    (newAreaOpacity) =>
      onStepChange(
        produce(step, (draft) => {
          draft.area_opacity = newAreaOpacity;
        })
      ),
    { delayMs: SLIDER_INPUT_DELAY_MS }
  );
  const handleAreaOpacityChange = (_: Event, sliderValue: number | number[]) => {
    const newValue = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
    if (newValue !== undefined) {
      setAreaOpacity(newValue);
    }
  };

  return (
    <Stack>
      <Stack direction="row" alignItems="center" mb={1}>
        <Box flexGrow={0.5}>
          <Typography>Area opacity</Typography>
        </Box>
        <Box flexGrow={1}>
          <Slider
            value={areaOpacity}
            valueLabelDisplay="auto"
            step={0.1}
            marks
            min={0}
            max={1}
            onChange={handleAreaOpacityChange}
          />
        </Box>
      </Stack>
      <TextField
        inputProps={{ 'aria-label': 'enter threshold step label' }}
        defaultValue={step.name ?? ''}
        label="Label"
        fullWidth
        onBlur={(event) => handleLabelChange(event.currentTarget.value)}
      />
    </Stack>
  );
}
